import {
	OrganizationPreferences,
	UserPreferences,
	VelavuAPI,
	VelavuOrganization,
	VelavuUser,
} from "velavu-js-api";
import { useCancellableEffect } from "../../helper/hook-helper";
import { fetchUserAttributes } from "aws-amplify/auth";
import { useCallback, useState } from "react";
import deepMerge from "../../helper/object-helper";

export interface UseUserOrganizationResult {
	user: VelavuUser | undefined;
	updateVelavuUser: (user: Partial<VelavuUser>) => Promise<VelavuUser>;
	updateVelavuUserImage: (imageData: string | undefined) => void;

	organization?: VelavuOrganization;
	updateOrganization: (
		organization: Partial<VelavuOrganization>,
	) => Promise<VelavuOrganization>;
}

export function useUserOrganization(
	isValid: boolean,
): UseUserOrganizationResult {
	//Sync the user's user information
	const [velavuUser, setVelavuUser] = useState<VelavuUser | undefined>(
		undefined,
	);
	useCancellableEffect(
		(addPromise) => {
			if (!isValid) return;

			addPromise(
				(async () => {
					//Get the user details
					const userAttributes = await fetchUserAttributes();
					const userEmail = userAttributes.email!;
					return await VelavuAPI.users.getSpecificUser(userEmail);
				})(),
			).then(setVelavuUser);
		},
		[isValid, setVelavuUser],
	);

	//Sync the user's organization information
	const [organization, setOrganization] = useState<
		VelavuOrganization | undefined
	>(undefined);
	useCancellableEffect(
		(addPromise) => {
			if (!isValid) return;

			setOrganization(undefined);

			//Get the user's organization information
			addPromise(VelavuAPI.organizations.getOrganization()).then(
				setOrganization,
			);
		},
		[isValid, setOrganization],
	);

	const updateVelavuUser = useCallback(
		async (updatedUser: Partial<VelavuUser>): Promise<VelavuUser> => {
			if (velavuUser === undefined) {
				throw new Error("User is not yet loaded!");
			}

			const settingsUpdate = updatedUser.settings;
			const hasSettingsChanges =
				settingsUpdate !== undefined &&
				Object.keys(settingsUpdate).length > 0;

			//Merge the preferences, stamping lastUpdated whenever settings change
			const mergedUser: Partial<VelavuUser> = {
				...updatedUser,
				settings: deepMerge<Partial<UserPreferences>>(
					velavuUser.settings,
					hasSettingsChanges
						? {
								...settingsUpdate,
								lastUpdated: new Date().toISOString(),
							}
						: settingsUpdate,
				),
			};

			//Submit the new user
			const newUser = await VelavuAPI.users.updateSpecificUserSettings(
				velavuUser.email,
				mergedUser,
			);

			setVelavuUser({
				...velavuUser,
				name: newUser.name,
				settings: newUser.settings,
			});

			return newUser;
		},
		[velavuUser, setVelavuUser],
	);

	const updateVelavuUserImage = useCallback(
		async (imageData: string | undefined) => {
			if (velavuUser === undefined) {
				throw new Error("User is not yet loaded!");
			}

			if (imageData !== undefined) {
				const updatedUser = await VelavuAPI.users.uploadProfilePicture(
					velavuUser.email,
					imageData,
				);
				setVelavuUser(updatedUser);
			} else {
				await VelavuAPI.users.deleteProfilePicture(velavuUser.email);

				setVelavuUser((user) =>
					user === undefined
						? undefined
						: { ...user, profile_img_url: undefined },
				);
			}
		},
		[velavuUser, setVelavuUser],
	);

	const updateOrganization = useCallback(
		async (
			updatedOrganization: Partial<VelavuOrganization>,
		): Promise<VelavuOrganization> => {
			if (organization === undefined) {
				throw new Error("Organization is not yet loaded!");
			}

			//Merge the preferences
			const mergedOrganization: Partial<VelavuOrganization> = {
				...updatedOrganization,
				settings: deepMerge<OrganizationPreferences>(
					organization.settings ?? {},
					updatedOrganization?.settings,
				),
			};

			//Submit the new organization
			const newOrganization =
				await VelavuAPI.organizations.updateOrganization(
					mergedOrganization,
				);
			setOrganization(newOrganization);
			return newOrganization;
		},
		[organization, setOrganization],
	);

	return {
		user: velavuUser,
		updateVelavuUser,
		updateVelavuUserImage,
		organization,
		updateOrganization,
	};
}
